// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
/**
 * Pure graph algorithms for process landscape analysis.
 * No React dependencies — can be used in hooks, workers, or tests.
 */

// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated

export interface AdjacencyMaps {
  downstream: Map<string, Set<string>>;
  upstream: Map<string, Set<string>>;
}

/** Build directed adjacency maps from a list of edges. */
export function buildAdjacencyMaps(edges: { source: string; target: string }[]): AdjacencyMaps {
  const downstream = new Map<string, Set<string>>();
  const upstream = new Map<string, Set<string>>();

  for (const edge of edges) {
    if (!downstream.has(edge.source)) downstream.set(edge.source, new Set());
    downstream.get(edge.source)!.add(edge.target);

    if (!upstream.has(edge.target)) upstream.set(edge.target, new Set());
    upstream.get(edge.target)!.add(edge.source);
  }

  return { downstream, upstream };
}

/**
 * BFS from a seed node following both upstream and downstream edges.
 * Returns the set of all transitively connected process IDs.
 */
export function computeTransitiveChain(seedId: string, adjacency: AdjacencyMaps): Set<string> {
  const visited = new Set<string>([seedId]);

  // Downstream BFS
  const downQueue = [seedId];
  while (downQueue.length > 0) {
    const current = downQueue.pop()!;
    for (const next of adjacency.downstream.get(current) ?? []) {
      if (!visited.has(next)) {
        visited.add(next);
        downQueue.push(next);
      }
    }
  }

  // Upstream BFS
  const upQueue = [seedId];
  while (upQueue.length > 0) {
    const current = upQueue.pop()!;
    for (const prev of adjacency.upstream.get(current) ?? []) {
      if (!visited.has(prev)) {
        visited.add(prev);
        upQueue.push(prev);
      }
    }
  }

  return visited;
}

/**
 * Returns 1-hop neighbors (both upstream and downstream) that are NOT
 * already in `currentIds`.
 */
export function getDirectNeighbors(currentIds: Set<string>, adjacency: AdjacencyMaps): Set<string> {
  const neighbors = new Set<string>();

  for (const id of currentIds) {
    for (const next of adjacency.downstream.get(id) ?? []) {
      if (!currentIds.has(next)) neighbors.add(next);
    }
    for (const prev of adjacency.upstream.get(id) ?? []) {
      if (!currentIds.has(prev)) neighbors.add(prev);
    }
  }

  return neighbors;
}

/**
 * Topological sort (Kahn's algorithm) of a subgraph.
 * Only considers edges between nodes in `processIds`.
 * Falls back to original set order for disconnected components.
 */
export function topologicalSort(processIds: Set<string>, adjacency: AdjacencyMaps): string[] {
  // Build in-degree map scoped to the subgraph
  const inDegree = new Map<string, number>();
  for (const id of processIds) inDegree.set(id, 0);

  for (const id of processIds) {
    for (const next of adjacency.downstream.get(id) ?? []) {
      if (processIds.has(next)) {
        inDegree.set(next, (inDegree.get(next) ?? 0) + 1);
      }
    }
  }

  // Start with nodes that have no incoming edges within the subgraph
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    sorted.push(current);
    for (const next of adjacency.downstream.get(current) ?? []) {
      if (!processIds.has(next)) continue;
      const newDeg = (inDegree.get(next) ?? 1) - 1;
      inDegree.set(next, newDeg);
      if (newDeg === 0) queue.push(next);
    }
  }

  // If there are cycles, append remaining nodes
  if (sorted.length < processIds.size) {
    const sortedSet = new Set(sorted);
    for (const id of processIds) {
      if (!sortedSet.has(id)) sorted.push(id);
    }
  }

  return sorted;
}
